/*
 * Copyright (c) 2020. American Express Company
 */

import $ from 'jquery';
import _ from 'lodash';
import React, { Component, ReactComponentElement } from 'react';
import { isFirefox, isMobileOnly } from 'react-device-detect';
import { ChronoUnit, LocalDate, LocalDateTime } from '@js-joda/core';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
// @ts-ignore
import RefreshWorker from 'worker-loader!src/bw/workers/refresh'; // eslint-disable-line import/no-webpack-loader-syntax
// models and services
import { AccountModel } from 'src/types/account.model';
import { CardModel } from 'src/types/card.model';
import { CashFlowModel } from 'src/types/cash-flow.model';
import { CategoryModel } from 'src/types/category.model';
import { DebtModel } from 'src/types/debt.model';
import { IncomeSourceRuleModel } from 'src/types/income-source-rule.model';
import { IncomeSourceModel } from 'src/types/income-source.model';
import { IncomeAggregate } from 'src/types/income.model';
import { InstitutionModel } from 'src/types/institution.model';
import { InvestmentModel } from 'src/types/investment.model';
import { MatchModel } from 'src/types/match.model';
import { RealEstateModel } from 'src/types/real-estate.model';
import { RuleModel } from 'src/types/rule.model';
import { StatsModel } from 'src/types/stats.model';
import { TransactionModel } from 'src/types/transaction.model';
import { UserActionTypeModel } from 'src/types/user-action.type';
import { FullUserModel, UserUtils } from 'src/types/user.model';
import AuthService from '../../services/auth.service';
import CashFlowService from '../../services/cashflow.service';
import CategoryService from '../../services/category.service';
import IncomeSourceRuleService from '../../services/income-source-rule.service';
import IncomeSourceService from '../../services/income-source.service';
import { JaegerService } from 'src/services/jaeger.service';
import LiteralsService from '../../services/literals/LiteralsService';
import LogoutService from '../../services/logout.service';
import MatchService from '../../services/match.service';
import MixpanelService from '../../services/mixpanel.service';
import NavigationService from '../../services/navigation.service';
import PersonService from '../../services/person.service';
import PlaidService from '../../services/plaid.service';
import PlanService from '../../services/plan.service';
import RuleService from '../../services/rule.service';
import StorageService, { Storage } from '../../services/storage.service';
import SubRouteService from '../../services/sub-route.service';
import TransactionService from '../../services/transaction.service';
import UserService from '../../services/user.service';
import UnitService from '../../services/unit.service';
import UtilsService from '../../services/utils.service';

// factories
import CalcFactory from 'src/bw/factories/calc.factory';
import CardFactory from 'src/bw/factories/card.factory';
import DrawerFactory from 'src/bw/factories/drawer.factory';
import ExpensesFactory from 'src/bw/factories/expenses.factory';
import AuthFactory from 'src/bw/factories/auth.factory';
import IncomeFactory from 'src/bw/factories/income.factory';
import MentorFactory from 'src/bw/factories/mentor.factory';
import RouteFactory from 'src/bw/factories/route.factory';
import TransactionFactory from 'src/bw/factories/transaction.factory';

// components
import * as Snackbar from 'src/bw/libs/snackbar';
import { setUser } from 'src/bw/redux/actions/user';
import { getUser, getUserError, getUserPending } from 'src/bw/redux/selectors';
import BwDrawer from 'src/bw/bw-drawer/BW-Drawer';
import ConfirmationPanel from 'src/bw/confirmation-panel/ConfirmationPanel';
import JaegerContainer, { JaegerContext } from 'src/bw/project-jaeger/JaegerContainer';
import Mentor from '../../components/mentors/Mentor';
import fetchUser from 'src/bw/redux/actions/fetch-user';
import DateUtils from 'src/utils/date';
import WaitIcon from 'src/bw/wait-icon/WaitIcon';
import ReturningDialog from 'src/bw/returning-dialog/ReturningDialog';

// icons, constants and css
import { LOGOUT_TIMEOUT, LOGOUT_WARNING_TIMEOUT } from '../logout-settings';
import './Drawer.scss';
import 'src/components/login-form/LoginForm.scss';

import { ConfirmationInfo } from 'src/types/state-models/board.state.model';
import UrlHelper from 'src/utils/url.helper';
import { ALLOW_TEST_LOGIN } from 'src/config';
import Loading from 'src/components/atomic/atoms/loading/Loading';

export interface SortOrder {
    sort: string;
    order: number;
}
interface BoardState {
    starting: boolean;
    loadUserParam: object;
    redirecting: boolean;
    mentor: {
        state: string;
        goal: string;
        onlySelected: boolean;
        retireAge: number;
        retireColumn: number;
    };
    overlay: boolean;
    currentForm: 'login' | 'signup';
    dialogId: number;
    dialogCodename: string;
    mentorMentor: string;
    mentorMode: string;
    mentorState: string;
    mentorMood: string;
    mentorHeader: string;
    mentorBody: JSX.Element;
    mentorIcon: JSX.Element;
    mentorButtonOneText: string;
    // return type void threw warning, but it actually returns void
    mentorButtonOneHandler: () => any;
    mentorButtonTwoText: string;
    mentorButtonTwoHandler: () => any;
    mentorButtonThreeText: string;
    mentorButtonThreeHandler: () => any;
    mentorLearnMore: string;
    mentorOpenPopover: boolean;
    mentorAnchor: any;
    mentorPosition: string;
    mentorImage: string;
    mentorImageWidth: number;
    mentorImageHeight: number;
    mentorClickAway: boolean;
    mentorCloseAction: UserActionTypeModel;
    mentorClass: string;
    mentorInfo: string;
    mentorDangerous: boolean;
    mentorX: number;
    mentorY: number;
    mentorHeight: number;
    newPassword: string;
    newPasswordConfirmation: string;
    oldPassword: string;
    user: FullUserModel;
    users: FullUserModel[];
    oldNetWorth: number;
    oldTargetNetWorth: number;
    currentNetWorth: number;
    netWorth: number;
    targetNetWorth: number;
    openShare: boolean;
    shareUrl: string;
    editAge: boolean;
    openLongevity: boolean;
    openIntro: boolean;
    ageCursor: number;
    zoom: string;
    view: string;
    monthsPerPeriod: number;
    useOwnDataCounter: number;
    commitPromptCounter: number;
    accounts: AccountModel[];
    firstImport: boolean;
    drawerState: string;
    drawerProps: any;
    scrollDate: LocalDate;
    transactionSummary: any;
    transactionsCategory: string;
    currentInvestment: InvestmentModel;
    card: CardModel;
    currentTransactions: TransactionModel[];
    currentTransactionsStartDate: LocalDate;
    currentTransactionsEndDate: LocalDate;
    transactionCategory: string;
    transactionSort: SortOrder;
    transactionsChanged: boolean;
    selectedTransaction: TransactionModel;
    selectedTransactionGroup: number;
    categories: CategoryModel[];
    openedPayer: string;
    userAccounts: Map<number, AccountModel>;
    incomeSources: IncomeSourceModel[];
    currentIncomeSource: IncomeSourceModel;
    incomeChanged: boolean;
    dragInProgress: number;
    dragDate: LocalDate;
    dragWidth: number;
    goalsAdded: number;
    openConfirmation: boolean;
    confirmationInfo: ConfirmationInfo;
    waitMessages: string[];
    netWorthDelta: number;
    error: any;
    autoLogin: boolean;
    startupStep: number;
    showNextStep: boolean;
    signupSuccessCallback: () => void;
    hasIncome: boolean;
    hasExpenses: boolean;
    hasInvestments: boolean;
    hasDebts: boolean;
    returningDialogOpen: boolean;
    stats: StatsModel;
    jaegerContext: JaegerContext;
}

export class Board extends Component<any, BoardState> {
    public calcFactory: CalcFactory;
    public cardFactory: CardFactory;
    public drawerFactory: DrawerFactory;
    public expensesFactory: ExpensesFactory;
    public authFactory: AuthFactory;
    public incomeFactory: IncomeFactory;
    public mentorFactory: MentorFactory;
    public transactionFactory: TransactionFactory;
    public routeFactory: RouteFactory;
    public override state: BoardState = {
        starting: true,
        loadUserParam: {},
        redirecting: false,
        mentor: {
            state: JSON.parse(StorageService.getRaw('mentors-state', 'mentors', Storage.session)),
            goal: '',
            onlySelected: false,
            retireAge: 0,
            retireColumn: 0
        },
        overlay: false,
        currentForm: 'login' as const,
        dialogId: null as number,
        dialogCodename: '',
        mentorMentor: 'walter',
        mentorMode: '',
        mentorState: '',
        mentorMood: 'neutral',
        mentorHeader: LiteralsService.getLiteral('Mentors.Default.header'),
        mentorBody: LiteralsService.getJsxLiteral('Mentors.Default.body'),
        mentorIcon: null as JSX.Element,
        mentorButtonOneText: LiteralsService.getLiteral('Mentors.Default.buttonOne'),
        mentorButtonOneHandler: () => {},
        mentorButtonTwoText: LiteralsService.getLiteral('Mentors.Default.buttonTwo'),
        mentorButtonTwoHandler: () => {},
        mentorButtonThreeText: '',
        mentorButtonThreeHandler: () => {},
        mentorLearnMore: '',
        mentorOpenPopover: false,
        mentorAnchor: null as any,
        mentorPosition: 'left',
        mentorImage: '',
        mentorImageWidth: 0,
        mentorImageHeight: 0,
        mentorClickAway: true,
        mentorCloseAction: null as UserActionTypeModel,
        mentorClass: '',
        mentorInfo: '',
        mentorDangerous: false,
        mentorX: 0,
        mentorY: 0,
        mentorHeight: 0,
        newPassword: '',
        newPasswordConfirmation: '',
        oldPassword: '',
        user: null as FullUserModel,
        users: [] as FullUserModel[],
        oldNetWorth: 0,
        oldTargetNetWorth: 0,
        currentNetWorth: 0,
        netWorth: 0,
        targetNetWorth: 0,
        openShare: false,
        shareUrl: '',
        editAge: false,
        openLongevity: false,
        openIntro: false,
        ageCursor: 0,
        zoom: 'months',
        view: 'income-expense',
        monthsPerPeriod: 12,
        useOwnDataCounter: 0,
        commitPromptCounter: 0,
        accounts: [] as AccountModel[],
        firstImport: false,
        drawerState: 'start',
        drawerProps: {} as any,
        scrollDate: null as LocalDate,
        transactionSummary: {
            sorted_categories: [] as any[],
            startDate: null as LocalDate,
            endDate: null as LocalDate,
            cashFlowDetailPopFrom: null as Object,
            label: '' as string
        },
        transactionsCategory: '',
        currentInvestment: null as InvestmentModel,
        card: undefined as CardModel,
        currentTransactions: [] as TransactionModel[],
        currentTransactionsStartDate: null as LocalDate,
        currentTransactionsEndDate: null as LocalDate,
        transactionCategory: '',
        transactionSort: { sort: 'date', order: 1 } as SortOrder,
        transactionsChanged: false,
        selectedTransaction: null as TransactionModel,
        selectedTransactionGroup: 0,
        categories: [] as CategoryModel[],
        openedPayer: null as string,
        userAccounts: null as Map<number, AccountModel>,
        incomeSources: null as IncomeSourceModel[],
        currentIncomeSource: null as IncomeSourceModel,
        incomeChanged: false,
        dragInProgress: 0,
        dragDate: null as LocalDate,
        dragWidth: 0 as number,
        goalsAdded: 0 as number,
        openConfirmation: false,
        confirmationInfo: null as ConfirmationInfo,
        waitMessages: [] as string[],
        netWorthDelta: 0,
        error: null as any,
        autoLogin: false,
        startupStep: null as number,
        showNextStep: true,
        signupSuccessCallback: null as () => void,
        hasIncome: true,
        hasExpenses: true,
        hasInvestments: true,
        hasDebts: true,
        returningDialogOpen: false,
        stats: null as StatsModel,
        jaegerContext: null as JaegerContext
    };

    private logoutWarningTimeout: number;
    private logoutTimeout: number;

    private readonly instance: RefreshWorker;
    private readonly instanceInterval: number;
    public surveyTimeout: number;

    constructor(props: any) {
        super(props);
        Board.notifyOnRoute();
        this.calcFactory = new CalcFactory(this);
        this.cardFactory = new CardFactory(this);
        this.drawerFactory = new DrawerFactory(this);
        this.expensesFactory = new ExpensesFactory(this);
        this.authFactory = new AuthFactory(this);
        this.incomeFactory = new IncomeFactory(this);
        this.mentorFactory = new MentorFactory(this);
        this.transactionFactory = new TransactionFactory(this);
        this.routeFactory = new RouteFactory(this);

        this.instance = new RefreshWorker();
        if (isFirefox) this.mentorFactory.FirefoxWarningDialog().catch(console.error);
    }

    public static mapStateToProps(state: any): any {
        // pending

        const pending: boolean = getUserPending(state);

        // user
        let user: FullUserModel;
        const userPayload = getUser(state).user;
        if (userPayload) user = UserUtils.deserializeDates(userPayload);
        return {
            error: getUserError(state),
            user,
            pending
        };
    }

    public static mapDispatchToProps(dispatch: any): any {
        return bindActionCreators(
            {
                fetchUser,
                setUser
            },
            dispatch
        );
    }

    /**
     * Return the states that need to change based on the given parameters
     * @param params
     */
    public static handleParams(params: any): any {
        if (!params) return null;

        let result = { ...params };
        // pull out URL parameters that trigger temporary user creation, if present
        const loadUserParam = _.pick(params, UtilsService.LOAD_USER_PARAM);

        if (loadUserParam.guid === 'demo' || loadUserParam.guid === 'perm') {
            MixpanelService.track('Start Demo');
            result = {
                ...result,
                loadUserParam: _.defaults(loadUserParam, { guid: loadUserParam.guid }), // default guid to demo
                redirecting: true
            };
        } else if (loadUserParam.guid === 'blank') {
            MixpanelService.track('S0 - Initial Visit');
            result = {
                ...result,
                loadUserParam: _.defaults(loadUserParam, { guid: 'blank' }), // default guid to blank
                openIntro: false
            };
        } else if (loadUserParam.guid === 'blank2') {
            MixpanelService.track('S0 - Initial Visit');
            result = {
                ...result,
                loadUserParam: _.defaults(loadUserParam, { guid: 'blank2' }), // default guid to blank
                openIntro: false
            };
        } else if (loadUserParam.guid) {
            // if a guid was given, use that to load the snapshot user
            result = {
                ...result,
                loadUserParam: _.defaults(loadUserParam, { guid: loadUserParam.guid }),
                redirecting: true
            };
        }

        switch (params.flow) {
            case 'welcome':
                break;
            case 'buyhome':
                const mentorState: any = { state: 'buyHomeDemo' };
                StorageService.setString('mentors-state', 'mentors', JSON.stringify(mentorState), Storage.session);
                result = {
                    ...result,
                    mentor: mentorState
                };
                break;
        }

        return result;
    }

    /**
     * This static method is called before render but does not have current object context.
     * given props and previous state, it returns the subset of the state that needs
     * to change. This makes this a more predictable state machine.
     *
     * @param props
     * @param prevState
     */
    public static getDerivedStateFromProps(props: any, prevState: any): any {
        if (window.location.hash || window.location.search) {
            const params = SubRouteService.getBoardRoute(props, prevState);
            return Board.handleParams(params);
        } else if (prevState.redirecting) {
            // if we were redirecting, then we loaded a new user
            if (!_.isEmpty(prevState.loadUserParam)) {
                // we loaded the user guid last time through, clear the user guid
                return { loadUserParam: {} };
            } else if (prevState.user?.id) {
                // we have a user, we are done redirecting
                return { redirecting: false };
            }
        }
        return null;
    }

    /**
     * This element is going away, stop listening to things
     */
    public override componentWillUnmount(): void {
        document.removeEventListener('keydown', this.mentorFactory.demoKeySequenceListener);
        window.clearInterval(this.instanceInterval);
    }

    /**
     * This element updated, let's see if we need to trigger anything for next time around loop
     */
    public override componentDidUpdate(prevProps: any): void {
        if (this.props.pending && !prevProps.pending) {
            this.drawerFactory.showLoader(['Loading your info…', 'This can take a few seconds.']);
        } else if (prevProps.pending && !this.props.pending) {
            this.drawerFactory.hideLoader();
        }

        // TODO: remove setting the incomeSources on update once card validity is tracked on the backend
        if (this.props.user && !this.state.incomeSources) {
            this.setIncomeSources().catch(console.error);
        }

        const newRoute = _.get(this.props, 'subRoute');
        const oldRoute = _.get(prevProps, 'subRoute');

        /* eslint eqeqeq: 0 */
        if (this.props.user && newRoute !== oldRoute) {
            this.routeFactory.handleRoute().catch(console.error);
        }
    }

    /**
     *  This element finished mounting (loaded for the first time), deal with startup things
     *  like loading the user or getting initial state
     */
    public override componentDidMount(): void {
        const guid: string = (this.state.loadUserParam as any).guid;
        if (guid !== 'blank2' && guid !== 'blank' && guid !== 'demo') {
            // TODO: perhaps replace this with something better when we set NSZ user attributes
            if (window.performance.navigation.type === 1 && this.props.user && this.props.user.temporary) {
                // window.performance.navigation.type is TYPE_RELOAD
                UrlHelper.replacePath('?guid=blank&redirect=true');
            } else if (window.performance.navigation.type === 0 && this.props.user && this.props.user.temporary) {
                // window.performance.navigation.type is TYPE_NAVIGATE
                const urlParams = new URLSearchParams(window.location.search);
                if (!urlParams.get('redirect')) {
                    AuthService.logoutUser().catch(console.error);
                } else {
                    urlParams.delete('redirect');
                    window.history.replaceState(null, '', `?${urlParams}${window.location.hash}`);
                }
            }
        }

        document.addEventListener('keydown', this.mentorFactory.demoKeySequenceListener.bind(this.mentorFactory));

        // done starting, so we can stop any "starting up/loading..." messages
        this.setState({ starting: false });

        if (this.props.user) {
            this.routeFactory.handleRoute().catch(console.error);
            this.calcFactory.calculateNetworth(this.state.ageCursor, this.props.user).catch(console.error);
            if (guid !== 'blank2' && guid !== 'blank' && guid !== 'demo' && !this.logoutWarningTimeout) {
                this.startLogoutTimers();
            }
        }

        // deal with any triggers (like passed parameters)
        this.hashTriggers();
        this.setSpecialDrawers().catch(console.error);

        if (this.state.autoLogin) {
            this.setState({
                autoLogin: false
            });
        }

        this.restartHeartBeat();
    }

    public async setSpecialDrawers(): Promise<void> {
        if (!(window.location.hash || window.location.search)) return;
        const allParams: any = SubRouteService.parseQuery(window.location.hash || window.location.search);
        if (!allParams.drawer) return;
        switch (allParams.drawer) {
            case 'match':
                const incomeSources: IncomeSourceModel[] = await IncomeSourceService.getIncomeSources();
                const match: MatchModel = await MatchService.create();
                this.drawerFactory.setDrawerRoute('match', {
                    user: this.props.user,
                    incomeSources,
                    match,
                    handleRefresh: this.handleReloadUser.bind(this)
                });
                break;
        }
    }

    public async setIncomeSources(): Promise<void> {
        const incomeSources: IncomeSourceModel[] = await IncomeSourceService.getIncomeSources();
        this.setState({ incomeSources });
    }

    /**
     * Deal with things that are triggered by hash (#) parameters This happens when the element first starts
     * and anytime there is an update, like reload of the page or change or url.
     */
    public hashTriggers(): void {
        if (!(window.location.hash || window.location.search)) return;

        if (!_.isEmpty(this.state.loadUserParam)) {
            // we are loading a user guid, create the user and clear the trigger state
            this.createUser(this.state.loadUserParam).catch(console.error);
            this.setState({ loadUserParam: {} });
        }

        const allParams: any = SubRouteService.parseQuery(window.location.hash || window.location.search);
        const strippedParams: any = _.omit(allParams, UtilsService.LOAD_USER_PARAM);
        if (_.size(allParams) !== _.size(strippedParams)) {
            // if we stripped params, replace the window location so we don't reuse launch parameters
            this.props.history.replace(window.location.pathname + '?' + SubRouteService.stringifyQuery(strippedParams));
        }
    }

    /**
     * This is called after the Board mounts for the first time.
     */
    private initialLoadingView(): JSX.Element {
        if (ALLOW_TEST_LOGIN && StorageService.getTestUserId()) {
            // Any user here will already have logged in via the separate login page, or loaded from guid
            this.authFactory.afterLoginSetup().catch(console.error);
        } else {
            this.authFactory
                .login()
                .then(() => {})
                .catch(console.error);
        }
        return <Loading />;
    }

    public override render(): ReactComponentElement<any> {
        /** If the mentor is a danger popover, only display it if the timeline is visible */
        const displayMentor: boolean = this.state.mentorDangerous ? JaegerService.timelineVisible() : true;

        let lists: JSX.Element = null;
        const cards = _.get(this.props, 'user.cards');
        if (cards) {
            const timelineType: JSX.Element = (
                <JaegerContainer
                    width={UnitService.getViewportWidth() - UnitService.getDrawerWidth()}
                    user={this.props.user}
                    cardFactory={this.cardFactory}
                    history={this.props.history}
                    zoom={this.state.zoom || 'years'}
                    handleClearUserAttribute={this.handleClearUserAttribute.bind(this)}
                    processAction={this.mentorFactory.processAction.bind(this.mentorFactory)}
                    view={this.state.view}
                    handleView={this.handleView.bind(this)}
                    context={this.state.jaegerContext}
                    updateJaegerContext={this.updateJaegerContext.bind(this)}
                    months={this.props.user.rawCashFlow.length}
                    startupStep={this.props.user.attributes.startupStep}
                    firstDrag={this.firstDrag.bind(this)}
                    handleUpdateUserAttributes={() => {
                        void this.handleUpdateUserAttributes.bind(this);
                    }}
                    handleReloadUser={this.handleReloadUser.bind(this)}
                />
            );

            lists = (
                <div>
                    {displayMentor && (
                        <Mentor
                            dialogId={this.state.dialogId}
                            key="mentor"
                            mentor={this.state.mentorMentor}
                            mode={this.state.mentorMode}
                            mood={this.state.mentorMood}
                            header={this.state.mentorHeader}
                            body={this.state.mentorBody}
                            icon={this.state.mentorIcon}
                            open={this.state.mentorOpenPopover}
                            anchor={this.state.mentorAnchor}
                            position={this.state.mentorPosition}
                            image={this.state.mentorImage}
                            imageHeight={this.state.mentorImageHeight}
                            imageWidth={this.state.mentorImageWidth}
                            buttonOneText={this.state.mentorButtonOneText}
                            buttonOneHandler={this.state.mentorButtonOneHandler()}
                            buttonTwoText={this.state.mentorButtonTwoText}
                            buttonTwoHandler={this.state.mentorButtonTwoHandler()}
                            buttonThreeText={this.state.mentorButtonThreeText}
                            buttonThreeHandler={this.state.mentorButtonThreeHandler()}
                            dismissMentors={this.mentorFactory.dismissMentors.bind(this.mentorFactory)}
                            learnMore={this.state.mentorLearnMore}
                            clickAway={this.state.mentorClickAway}
                            closeAction={this.state.mentorCloseAction}
                            handleReloadUser={this.handleReloadUser.bind(this)}
                            mentorClass={this.state.mentorClass}
                            info={this.state.mentorInfo}
                            dangerous={this.state.mentorDangerous}
                            mentorX={this.state.mentorX}
                            mentorY={this.state.mentorY}
                            mentorHeight={this.state.mentorHeight}
                            user={this.props.user}
                        />
                    )}
                    <BwDrawer
                        key="bw-drawer"
                        ageCursor={this.state.ageCursor}
                        context={this.state.jaegerContext}
                        currentNetWorth={this.state.currentNetWorth}
                        dismissMentors={this.mentorFactory.dismissMentors.bind(this.mentorFactory)}
                        drawer={this.state.drawerState}
                        drawerProps={this.state.drawerProps}
                        filterSummaries={this.expensesFactory.filterSummaries.bind(this.expensesFactory)}
                        firstImport={this.state.firstImport}
                        getCards={this.getCards.bind(this)}
                        getIncome={this.getIncome.bind(this)}
                        goToCard={this.goToCard.bind(this)}
                        mentor={this.state.mentor}
                        monthsPerPeriod={this.state.monthsPerPeriod}
                        netWorthDelta={this.state.netWorthDelta}
                        setDrawer={this.drawerFactory.setDrawerRoute.bind(this.drawerFactory)}
                        onboardingRetireAge={this.state.mentor.retireAge}
                        updateDrawer={this.drawerFactory.updateDrawer.bind(this.drawerFactory)}
                        showLoader={this.drawerFactory.showLoader.bind(this.drawerFactory)}
                        hasData={this.hasData.bind(this)}
                        hideLoader={this.drawerFactory.hideLoader.bind(this.drawerFactory)}
                        showNextStep={this.state.showNextStep}
                        user={this.props.user}
                        zoom={this.state.zoom}
                        handleAddDebt={this.handleAddDebt.bind(this)}
                        handleAddCrypto={this.handleAddCrypto.bind(this)}
                        handleAddExpense={this.expensesFactory.handleAddExpense.bind(this.expensesFactory)}
                        handleAddIncome={this.incomeFactory.handleAddIncome.bind(this.incomeFactory)}
                        handleAddIncomeRule={this.incomeFactory.handleAddIncomeRule.bind(this.incomeFactory)}
                        handleAddInvestment={this.handleOpenAssetsInvestments.bind(this)}
                        handleBack={this.drawerFactory.handleBack.bind(this.drawerFactory)}
                        handleCancelIncome={this.incomeFactory.handleCancelIncome.bind(this.incomeFactory)}
                        handleCreateCard={this.cardFactory.handleCreateAndOpenCard.bind(this.cardFactory)}
                        handleChangeCard={this.cardFactory.handleChangeCard.bind(this.cardFactory)}
                        handleChangeCardDate={this.cardFactory.handleChangeCardDate.bind(this.cardFactory)}
                        handleChangeCardValue={this.cardFactory.handleChangeCardValue.bind(this.cardFactory)}
                        handleChangeCardAttribute={this.cardFactory.handleChangeCardAttribute.bind(this.cardFactory)}
                        handleChangeCardAttributes={this.cardFactory.handleChangeCardAttributes.bind(this.cardFactory)}
                        handleChangeUser={this.handleChangeUser.bind(this)}
                        handleCreateLinkedSavingsCard={this.cardFactory.handleCreateLinkedSavingsCard.bind(
                            this.cardFactory
                        )}
                        handleDeleteLinkedSavingsCard={this.cardFactory.handleDeleteLinkedSavingsCard.bind(
                            this.cardFactory
                        )}
                        handleDeleteCard={this.cardFactory.handleDeleteCard.bind(this.cardFactory)}
                        handleDeleteRule={this.handleDeleteRule.bind(this)}
                        handleDeleteIncomeRule={this.handleDeleteIncomeRule.bind(this)}
                        handleUpdateCard={this.cardFactory.handleUpdateCard.bind(this.cardFactory)}
                        handleClearFirstImport={this.handleClearFirstImport.bind(this)}
                        handleDeleteIncome={this.incomeFactory.handleDeleteIncome.bind(this.incomeFactory)}
                        handleDisplayIncome={this.incomeFactory.handleDisplayIncome.bind(this.incomeFactory)}
                        handleAccounts={this.handleAccounts.bind(this)}
                        handleEditAccount={this.handleEditAccount.bind(this)}
                        handleEditDebt={this.handleEditDebt.bind(this)}
                        handleEditIncome={this.incomeFactory.handleEditIncome.bind(this.incomeFactory)}
                        handleEditInvestment={this.handleEditInvestment.bind(this)}
                        handleEditRealEstate={this.handleEditRealEstate.bind(this)}
                        handleFirstImport={this.handleFirstImport.bind(this)}
                        handleIncomeChange={this.incomeFactory.handleIncomeChange.bind(this.incomeFactory)}
                        handleIncomeDateChange={this.incomeFactory.handleIncomeDateChange.bind(this.incomeFactory)}
                        handleOpenCard={this.cardFactory.handleOpenCard.bind(this.cardFactory)}
                        handleOpenConfirmation={this.handleOpenConfirmation.bind(this)}
                        handleOpenExpenses={this.expensesFactory.handleOpenExpenses.bind(this.expensesFactory)}
                        handleOpenIncome={this.incomeFactory.handleOpenIncome.bind(this.incomeFactory)}
                        handleOpenIncomeSummary={this.incomeFactory.handleOpenIncomeSummary.bind(this.incomeFactory)}
                        handleOpenLinkedCard={this.cardFactory.handleOpenLinkedCard.bind(this.cardFactory)}
                        handleOpenPayer={this.handleOpenPayer.bind(this)}
                        handleOpenRecurringExpense={this.expensesFactory.handleOpenRecurringExpense.bind(
                            this.expensesFactory
                        )}
                        handleOpenRules={this.handleOpenRules.bind(this)}
                        handleOpenIncomeRules={this.handleOpenIncomeRules.bind(this)}
                        handleOpenTransactions={this.expensesFactory.handleOpenTransactions.bind(this.expensesFactory)}
                        handleOpenTransactionWithId={this.transactionFactory.handleOpenTransactionWithId.bind(
                            this.transactionFactory
                        )}
                        handleOpenReviewIncomeTransactions={this.incomeFactory.handleOpenReviewIncomeTransactions.bind(
                            this.incomeFactory
                        )}
                        handleOpenReviewExpenseTransactions={this.expensesFactory.handleOpenReviewExpenseTransactions.bind(
                            this.expensesFactory
                        )}
                        handleReconnectInstitution={this.handleReconnectInstitution.bind(this)}
                        handleReloadUser={this.handleReloadUser.bind(this)}
                        handleSaveIncome={this.incomeFactory.handleSaveIncome.bind(this.incomeFactory)}
                        handleShowWorkAssets={this.handleShowWorkAssets.bind(this)}
                        handleShowWorkDebts={this.handleShowWorkDebts.bind(this)}
                        handleShowWorkExpenses={this.handleShowWorkExpenses.bind(this)}
                        handleShowWorkIncomes={this.handleShowWorkIncomes.bind(this)}
                        handleShowWorkNetworth={this.handleShowWorkNetworth.bind(this)}
                        handleTopDrawer={this.drawerFactory.handleTopDrawer.bind(this.drawerFactory)}
                        handleUpdateRule={this.handleUpdateRule.bind(this)}
                        handleUpdateIncomeRule={this.handleUpdateIncomeRule.bind(this)}
                        handleUpdateUser={this.handleUpdateUser.bind(this)}
                        handleUpdateUserAttribute={this.handleUpdateUserAttribute.bind(this)}
                        handleUpdateUserAttributes={this.handleUpdateUserAttributes.bind(this)}
                        handleView={this.handleView.bind(this)}
                        handleClearUserAttribute={this.handleClearUserAttribute.bind(this)}
                        handleZoom={this.handleZoom.bind(this)}
                        hideNextStep={this.hideNextStep.bind(this)}
                        triggerGoalDrawer={this.mentorFactory.triggerGoalDrawer.bind(this.mentorFactory)}
                        setMentorState={this.mentorFactory.setMentorState.bind(this.mentorFactory)}
                        setRetireAge={this.mentorFactory.setRetireAge.bind(this.mentorFactory)}
                        setTransactions={this.transactionFactory.setTransactions.bind(this.transactionFactory)}
                        updateFilteredTransactions={this.transactionFactory.updateFilteredTransactions.bind(
                            this.transactionFactory
                        )} // TODO: this should be removed once we move to the new Transactions View after refresh
                        handleChangeIncomeSource={this.transactionFactory.handleChangeIncomeSource.bind(
                            this.transactionFactory
                        )} // TODO: this should be removed once we move to the new Transactions View after refresh
                        handleDisplayTransaction={this.transactionFactory.handleDisplayTransaction.bind(
                            this.transactionFactory
                        )} // TODO: this should be removed once we move to the new Transactions View after refresh
                        handleIgnoreFutureTransaction={this.transactionFactory.handleIgnoreFutureTransaction.bind(
                            this.transactionFactory
                        )} // TODO: this should be removed once we move to the new Transactions View after refresh
                        handleSortUnifiedTransaction={this.transactionFactory.handleSortUnifiedTransaction.bind(
                            this.transactionFactory
                        )} // TODO: this should be removed once we move to the new Transactions View after refresh
                        handleChangeTransactionGroup={this.transactionFactory.handleChangeTransactionGroup.bind(
                            this.transactionFactory
                        )} // TODO: this should be removed once we move to the new Transactions View after refresh
                        recategorizeSelectedIncomes={this.transactionFactory.recategorizeSelectedIncomes.bind(
                            this.transactionFactory
                        )} // TODO: this should be removed once we move to the new Transactions View after refresh
                        recategorizeSelectedExpenses={this.transactionFactory.recategorizeSelectedExpenses.bind(
                            this.transactionFactory
                        )} // TODO: this should be removed once we move to the new Transactions View after refresh
                        changeFrequenceOfSelected={this.transactionFactory.changeFrequenceOfSelected.bind(
                            this.transactionFactory
                        )} // TODO: this should be removed once we move to the new Transactions View after refresh
                        updateAfterRulesChange={this.updateAfterRulesChange.bind(this)}
                        startupStep={this.state.startupStep}
                        updateStartStep={this.updateStartStep.bind(this)}
                        mentorDisplayed={!!this.state.mentorClass}
                        validIncomeSources={this.incomeFactory.validIncomeSources.bind(this.incomeFactory)}
                        validIncomeSourcesToEnd={this.incomeFactory.validIncomeSourcesToEnd.bind(this.incomeFactory)}
                        incrementStartStep={this.incrementStartStep.bind(this)}
                        processAction={this.mentorFactory.processAction.bind(this.mentorFactory)}
                        hasIncome={this.state.hasIncome}
                        hasExpenses={this.state.hasExpenses}
                        hasInvestments={this.state.hasInvestments}
                        hasDebts={this.state.hasDebts}
                        history={this.props.history}
                        updateUserLocally={this.updateUserLocally.bind(this)}
                        transactionFactory={this.transactionFactory}
                    />
                    {timelineType}
                </div>
            );
        }

        let activeView;
        if (this.state.starting) {
            // TODO: this be a good place for a "loading..." page
            activeView = null;
        } else if (this.props.user) {
            activeView = lists;
        } else if (this.state.redirecting) {
            // TODO: this be another good place for a "loading..." page
            activeView = null;
        } else {
            activeView = this.initialLoadingView();
        }

        let className: string = 'board';
        if (window.innerHeight > window.innerWidth) {
            className += ' portrait';
        } else {
            className += ' landscape';
        }
        if (isMobileOnly) {
            className += ' mobile';
        }

        return (
            <React.Fragment>
                <WaitIcon key="wait-icon" messages={this.state.waitMessages} />
                <div
                    key="main-overlay"
                    className="main-overlay"
                    style={{ display: this.state.overlay ? 'block' : 'none' }}
                />
                <div className={className} key="board">
                    <ConfirmationPanel
                        key="confirmation-panel"
                        open={this.state.openConfirmation}
                        handleCloseConfirmation={this.handleCloseConfirmation.bind(this)}
                        confirmationInfo={this.state.confirmationInfo}
                    />
                    <ReturningDialog
                        history={this.props.history}
                        onClose={() => this.setState({ returningDialogOpen: false })}
                        open={this.state.returningDialogOpen}
                        stats={this.state.stats}
                        handleOpenIncomeSummary={this.incomeFactory.handleOpenIncomeSummary.bind(this.incomeFactory)}
                        handleOpenExpenses={this.expensesFactory.handleOpenExpenses.bind(this.expensesFactory)}
                        handleOpenTransactions={this.expensesFactory.handleOpenTransactions.bind(this.expensesFactory)}
                        handleOpenTransactionWithId={this.transactionFactory.handleOpenTransactionWithId.bind(
                            this.transactionFactory
                        )}
                        status={this.props.user?.status}
                    />
                    {activeView}
                </div>
            </React.Fragment>
        );
    }

    // linter suggests this can be static, but that does not work
    // noinspection JSMethodCanBeStatic
    private async getIncome(): Promise<number> {
        let incomeSources: IncomeSourceModel[] = await IncomeSourceService.getIncomeSources();
        incomeSources = _.orderBy(incomeSources, ['amount'], ['asc']);
        return incomeSources.length > 0 ? incomeSources[0].annualizedIncome : 0;
    }

    public updateUser(): FullUserModel {
        const user: FullUserModel = StorageService.getReduxUserObject();
        void this.calcFactory.calculateNetworth(this.state.ageCursor, user);
        return user;
    }

    // update the user without calling the backend
    public updateUserLocally(updatedUser: FullUserModel): void {
        this.props.setUser(updatedUser);
        this.setState({
            user: updatedUser
        });
    }

    public async handleOpenRules(): Promise<any> {
        const rules = await RuleService.getRules();
        const categories: CategoryModel[] = await CategoryService.getCategories();

        this.drawerFactory.setDrawerRoute('rules', {
            rules,
            categories
        });
    }

    public async handleOpenIncomeRules(): Promise<any> {
        const rules = await IncomeSourceRuleService.getRules();
        const incomeSources = await IncomeSourceService.getIncomeSources();

        this.drawerFactory.setDrawerRoute('income-rules', {
            rules,
            incomeSources
        });
    }

    private async updateAfterRulesChange(): Promise<void> {
        const rules: RuleModel[] = await RuleService.getRules();
        this.drawerFactory.updateDrawer({
            rules
        });
    }

    private async handleUpdateRule(rule: RuleModel): Promise<void> {
        const updatedRule: RuleModel = await RuleService.updateRule(rule);

        const rules: RuleModel[] = this.state.drawerProps.rules;
        const index = _.findIndex(rules, { id: rule.id });
        rules.splice(index, 1, updatedRule);

        this.drawerFactory.updateDrawer({
            ...this.state.drawerProps,
            rules
        });
    }

    private async handleUpdateIncomeRule(rule: IncomeSourceRuleModel): Promise<void> {
        const updatedRule: IncomeSourceRuleModel = await IncomeSourceRuleService.updateRule(rule);

        const rules: IncomeSourceRuleModel[] = this.state.drawerProps.incomeRules;
        const index = _.findIndex(rules, { incomeRuleUid: rule.incomeRuleUid });
        rules.splice(index, 1, updatedRule);

        this.drawerFactory.updateDrawer({
            ...this.state.drawerProps,
            incomeRules: rules
        });
    }

    private async handleDeleteRule(rule: RuleModel): Promise<void> {
        await RuleService.deleteRule(rule.id);

        const rules: RuleModel[] = this.state.drawerProps.rules;
        _.remove(rules, { id: rule.id });

        this.drawerFactory.updateDrawer({
            ...this.state.drawerProps,
            rules
        });
    }

    private async handleDeleteIncomeRule(rule: IncomeSourceRuleModel): Promise<void> {
        await IncomeSourceRuleService.deleteRule(rule);

        const rules: IncomeSourceRuleModel[] = this.state.drawerProps.incomeRules;
        _.remove(rules, { incomeRuleUid: rule.incomeRuleUid });

        this.drawerFactory.updateDrawer({
            ...this.state.drawerProps,
            incomeRules: rules
        });
    }

    private static notifyOnRoute(): void {
        const href = window.location.href;
        if (href.includes('confirm-email')) {
            Snackbar.show({
                text: "We've just sent you a validation email. Please consult your email and click the link therein."
            });
        } else if (href.includes('sent-recovery-email')) {
            Snackbar.show({
                text: 'Please check your email. We have sent you account recovery instructions.'
            });
        } else if (href.includes('password-reset')) {
            Snackbar.show({
                text:
                    'We have just emailed you a new temporary password. You may use this to login, and will be ' +
                    'prompted to change it.'
            });
        }
    }

    public handleZoom(zoom: string, user: FullUserModel = null, changeDefault: boolean = false): void {
        if (!user) {
            user = this.props.user;
        }

        StorageService.setZoom(zoom);
        const monthsPerPeriod: number = UserService.zoomCashFlow(user);

        if (changeDefault) void this.handleUpdateUserAttribute('zoom', zoom);

        this.setState({
            user,
            zoom,
            monthsPerPeriod
        });
    }

    private async handleChangeUser(value: any, name: string, type: string = 'none'): Promise<void> {
        if (!this.props.user) {
            return; // too soon
        }
        const newUser: FullUserModel = { ...this.props.user };
        switch (type) {
            case 'localdate':
                value = DateUtils.fromDateToLocalDate(value);
                break;
        }
        (newUser as any)[name] = value;

        switch (name) {
            case 'dob':
                // this.mentorFactory.triggerRetirement(value);
                break;
        }

        await UserService.updateUser(newUser);
        await this.handleReloadUser();
    }

    private async createUser(params: any): Promise<void> {
        const guid: string = _.get(params, 'guid');
        const response = await AuthService.createTempUser(params);
        StorageService.setTestUserId(response.userUid);
        await this.handleReloadUser();
        this.restartHeartBeat();

        // allow for deep URIs in demo session
        if (guid === 'demo' && window.location.pathname !== UrlHelper.getFullPath('/'))
            this.routeFactory.handleRoute().catch(console.error);
    }

    private getCards(): CardModel[] {
        if (!this.props.user) {
            return [];
        }
        return this.props.user.cards;
    }

    public async handleReloadUser(login?: boolean): Promise<any> {
        let user: FullUserModel = null;

        try {
            this.drawerFactory.showLoader([LiteralsService.getLiteral('Loader.accountLine1')]);

            await this.props.fetchUser();
            user = this.props.user;

            this.handleZoom('years', user);

            if (user) {
                await this.calcFactory.calculateNetworth(this.state.ageCursor, user);
                user.persons = PersonService.getPersonsFromUser(user);
                user.currentPerson = _.find(user.persons, { relationship: 'self' });
            }

            this.setState({
                hasIncome: Boolean(_.sumBy(user.cashFlow, 'income')),
                hasExpenses: Boolean(_.sumBy(user.cashFlow, 'expense')),
                hasInvestments: Boolean(_.sumBy(user.cashFlow, 'investment')),
                hasDebts: Boolean(_.sumBy(user.cashFlow, 'debt'))
            });

            if (login && user) {
                this.handleLogin();
                this.restartHeartBeat();
            }
        } catch (e: unknown) {
            console.error(e);
        } finally {
            this.drawerFactory.hideLoader();
        }

        // WMT-11242 Danger Mentor should not show up in onboarding
        // we were hitting this code before the attributes were setup
        if (
            user?.attributes.hideDangerBar !== undefined &&
            user &&
            !user.attributes.hideDangerBar &&
            !user.attributes.seenDangerBar &&
            _.find(user.rawCashFlow, { dangerZone: true })
        ) {
            await this.mentorFactory.processAction('intro-danger-bar');
        }

        return user;
    }

    private handleFirstImport(): void {
        this.setState({
            firstImport: true,
            overlay: true,
            waitMessages: ['Updating your account…', 'This can take up to five minutes.']
        });
    }

    private handleClearFirstImport(): void {
        this.setState({
            firstImport: false,
            overlay: false,
            waitMessages: []
        });
    }

    private async showReturningDialog(): Promise<void> {
        if (
            this.state.returningDialogOpen ||
            !this.props.user.status ||
            this.props.user.attributes.startupStep !== undefined
        )
            return;
        const attr: string = _.get(this.props, 'user.attributes.returningDialogLastShown');
        const institutions: InstitutionModel[] = _.get(this.props, 'user.institutions');
        // We only want to show the dialog to users with connected institutions who haven't seen it for six hours.
        if (!institutions || !institutions.length) return;
        const now: LocalDateTime = LocalDateTime.now();
        const performShow = async (): Promise<void> => {
            const stats: StatsModel = await PlanService.getSummary();
            this.setState({ returningDialogOpen: true, stats });
            await this.handleUpdateUserAttribute('returningDialogLastShown', now);
        };
        try {
            if (!attr) {
                await performShow();
            } else {
                const lastShown: LocalDateTime = LocalDateTime.parse(attr);
                if (lastShown.until(now, ChronoUnit.HOURS) >= 6) {
                    await performShow();
                }
            }
        } catch (e) {
            console.warn('Failed to parse last shown date:', e);
        }
    }

    public handleLogin(): void {
        void this.routeFactory.handleRoute();
        const oldNetworth: number = _.get(this.props, 'user.attributes.networth');
        if (oldNetworth !== this.props.user.currentNetWorth) {
            this.setState({
                netWorthDelta: this.props.user.currentNetWorth - oldNetworth
            });
        }

        const guid: string = (this.state.loadUserParam as any).guid;
        if (guid !== 'blank2' && guid !== 'blank' && guid !== 'demo') {
            this.startLogoutTimers();
            // pausing survey per APP-6810
            // this.startSurveyTimer();
            this.showReturningDialog().catch(console.error);
        }
    }

    public handleOpenPayer(aggregate: IncomeAggregate): void {
        const drawerProps: any = this.state.drawerProps;

        if (drawerProps.openedPayer === aggregate.payer) {
            this.drawerFactory.updateDrawer({
                ...this.state.drawerProps,
                openedPayer: null
            });
        } else {
            this.drawerFactory.updateDrawer({
                ...this.state.drawerProps,
                openedPayer: aggregate.payer
            });
        }
    }

    public onReturnToFindHomeModal(): void {
        this.drawerFactory.setDrawerRoute('find-home', {
            card: this.state.card
        });
    }

    public startSurveyTimer(): void {
        // user dismissed the survey in the past 5 days
        const daysPauseWhenDismissed = 5;
        const dismissedRecently: boolean =
            this.props.user.attributes.dismissedSurvey &&
            LocalDateTime.parse(this.props.user.attributes.dismissedSurvey).isAfter(
                LocalDateTime.now().minusDays(daysPauseWhenDismissed)
            );

        if (
            !this.props.user.attributes.logins ||
            this.props.user.attributes.logins < 2 ||
            this.props.user.attributes.surveyed ||
            dismissedRecently ||
            this.props.user.attributes.startupStep !== undefined
        ) {
            clearTimeout(this.surveyTimeout);
            return;
        }

        // delay if there is a mentor open
        // delay of you are in startup
        // delay if the returning dialog is open
        this.surveyTimeout = window.setTimeout(() => {
            if (
                this.state.mentorClass ||
                this.props.user.attributes.startupStep !== undefined ||
                this.state.returningDialogOpen
            ) {
                this.startSurveyTimer();
            }
        }, 60000);
    }

    /**
     * Auto-logout experience
     */
    public startLogoutTimers(): void {
        Board.stopTimeoutListeners();
        $('body').on('click.logtimers', () => {
            window.clearTimeout(this.logoutWarningTimeout);
            window.clearTimeout(this.logoutTimeout);
            this.startLogoutCountdown();
        });

        this.startLogoutCountdown();
    }

    private static stopTimeoutListeners(): void {
        $('body').off('click.logtimers');
    }

    private startLogoutCountdown(): void {
        window.clearTimeout(this.logoutWarningTimeout);
        window.clearTimeout(this.logoutTimeout);

        this.logoutWarningTimeout = window.setTimeout(() => {
            // Do not count clicks when mentor is open
            Board.stopTimeoutListeners();
        }, LOGOUT_WARNING_TIMEOUT); // 14 mins

        this.logoutTimeout = window.setTimeout(() => {
            MixpanelService.logout();
            LogoutService.handleLogoutRedirect();
        }, LOGOUT_TIMEOUT); // 15 mins
    }

    public handleOpenAssetsInvestments(): void {
        this.setState({
            currentInvestment: null
        });

        void NavigationService.goTo(this.props.history, '/investment', 'Investment drawer');
    }

    private handleEditRealEstate(realEstate: RealEstateModel): void {
        void NavigationService.goTo(this.props.history, `/realestate/${realEstate.id}`, 'Real Estate drawer');
    }

    private handleAddDebt(): void {
        void NavigationService.goTo(this.props.history, '/debt', 'Debt drawer');
    }

    private handleAddCrypto(): void {
        void NavigationService.goTo(this.props.history, '/crypto', 'Crypto drawer');
    }

    public handleEditInvestment(investment: InvestmentModel): void {
        if (investment.accountId) {
            const account: AccountModel = _.find(this.props.user.accounts, { id: investment.accountId });
            if (account) {
                void NavigationService.goTo(
                    this.props.history,
                    `/drawer/account/${account.accountId}`,
                    'Account Drawer'
                );
            }
        } else {
            void NavigationService.goTo(this.props.history, `/investment/${investment.id}`, 'Investment drawer');
        }
    }

    public handleEditDebt(debt: DebtModel): void {
        if (debt.accountId) {
            const account: AccountModel = _.find(this.props.user.accounts, { id: debt.accountId });
            if (account) {
                void NavigationService.goTo(
                    this.props.history,
                    `/drawer/account/${account.accountId}`,
                    'Account Drawer'
                );
            }
        } else {
            void NavigationService.goTo(this.props.history, `/debt/${debt.id}`, 'Debt drawer');
        }
    }

    private async handleEditAccount(account: AccountModel): Promise<void> {
        void NavigationService.goTo(this.props.history, `/drawer/account/${account.accountId}`, 'Account drawer');
    }

    public async handleAccounts(): Promise<void> {
        void NavigationService.goTo(this.props.history, '/drawer/profile', 'Accounts drawer');
    }

    private handleOpenConfirmation(confirmationInfo: ConfirmationInfo): void {
        this.setState({
            openConfirmation: true,
            confirmationInfo
        });
    }

    private handleCloseConfirmation(): void {
        this.setState({
            openConfirmation: false
        });
    }

    public async handleUpdateUserAttribute(name: string, value: any): Promise<void> {
        const newUser: FullUserModel = this.props.user;
        newUser.attributes[name] = value;
        const attributes: { [key: string]: any } = {};
        attributes[name] = value;

        await UserService.putAttributes(attributes);
        this.updateUserLocally(newUser);
    }

    public handleUpdateUser(user: FullUserModel): void {
        this.setState({
            user
        });
    }

    private async handleClearUserAttribute(name: string): Promise<void> {
        const newUser: FullUserModel = this.props.user;
        const attributes: any = newUser.attributes;

        delete attributes[name];
        await UserService.deleteAttribute(name);

        this.updateUserLocally(newUser);
    }

    private async handleUpdateUserAttributes(attributes: any): Promise<void> {
        const newUser: FullUserModel = this.props.user;
        newUser.attributes = attributes;

        await UserService.postAttributes(attributes);

        this.updateUserLocally(newUser);
    }

    private async handleReconnectInstitution(institution: InstitutionModel): Promise<void> {
        const token = await PlaidService.reconnectInstitution(institution.id);

        if (token) {
            const plaid = (window as any).Plaid;

            // Initialize Link with the token parameter
            // set to the generated public_token for the Item
            const linkHandler = plaid.create({
                token,
                onSuccess: this.reconnectInstitutionSuccess.bind(this),
                onExit: this.reconnectInstitutionExit.bind(this)
            });
            linkHandler.open();
        }
    }

    private async reconnectInstitutionExit(err: any, metadata: any): Promise<void> {
        console.log('err', err, metadata); // left in intentionally
        if (err.error_code === 'item-no-error') {
            const institution = _.find(this.props.user.institutions, {
                institutionId: metadata.institution.institution_id
            });
            try {
                await PlaidService.resetInstitutionLogin(institution.id);
            } catch (e) {
                console.error(e);
            }
            await this.handleReloadUser();
            await this.handleAccounts();
        }
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    private async reconnectInstitutionSuccess(_public_token: string, metadata: any): Promise<void> {
        const institution = _.find(this.props.user.institutions, {
            institutionId: metadata.institution.institution_id
        });
        try {
            await PlaidService.resetInstitutionLogin(institution.id);
        } catch (e) {
            console.error(e);
        }
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        setTimeout(this.finishReconnect.bind(this), 100);
    }

    private async finishReconnect(): Promise<void> {
        await this.handleReloadUser();
        await this.handleAccounts();
    }

    private handleView(view: string): void {
        this.setState({ view });
    }

    private async handleShowWorkAssets(cashFlow: CashFlowModel, handleBack?: () => void): Promise<void> {
        await this.handleShowWork('assets', cashFlow, handleBack);
    }

    private async handleShowWorkDebts(cashFlow: CashFlowModel, handleBack?: () => void): Promise<void> {
        await this.handleShowWork('debts', cashFlow, handleBack);
    }

    private async handleShowWorkIncomes(cashFlow: CashFlowModel, handleBack?: () => void): Promise<void> {
        await this.handleShowWork('incomes', cashFlow, handleBack);
    }

    public async handleShowWorkExpenses(cashFlow: CashFlowModel, handleBack?: () => void): Promise<void> {
        await this.handleShowWork('expenses', cashFlow, handleBack);
    }

    private async handleShowWorkNetworth(cashFlow: CashFlowModel, handleBack?: () => void): Promise<void> {
        await this.handleShowWork('networth', cashFlow, handleBack);
    }

    private async handleShowWork(dataType: string, cashFlow: CashFlowModel, handleBack?: () => void): Promise<void> {
        if (!cashFlow) {
            cashFlow = this.state.drawerProps.cashFlow;
        }
        if (!cashFlow.income && !cashFlow.expense && !cashFlow.freeCash) {
            return;
        }

        const zoom = this.state.zoom === 'months' ? 'month' : 'year';
        const drawer: string = 'show-work-' + dataType;
        let type: string = 'incomeExpense';
        let incomeSources: IncomeSourceModel[] = null;

        switch (dataType) {
            case 'incomes':
                incomeSources = await IncomeSourceService.getIncomeSources();
                break;
            case 'assets':
            case 'debts':
            case 'networth':
                type = 'assetDebt';
                break;
        }

        const cashFlowDetails = await CashFlowService.getCashflowDetails(type, zoom, cashFlow.startDate);

        this.drawerFactory.setDrawerRoute(drawer, {
            incomeSources,
            cashFlowDetails,
            zoom,
            startDate: cashFlow.startDate,
            endDate: cashFlow.endDate,
            cashFlow,
            handleBack
        });
    }

    public async getTransactions(startDate: LocalDate, endDate: LocalDate): Promise<TransactionModel[]> {
        const transactions: TransactionModel[] = await TransactionService.getTransactions(startDate, endDate);

        this.setState({
            currentTransactions: transactions,
            currentTransactionsStartDate: startDate,
            currentTransactionsEndDate: endDate
        });

        return transactions;
    }

    public updateTransactions(transaction: TransactionModel): void {
        const transactions = this.state.currentTransactions;
        const index = _.findIndex(transactions, { id: transaction.id });
        if (index !== -1) {
            transactions[index] = transaction;
        }

        this.setState({
            currentTransactions: transactions
        });
    }

    public async incrementStartStep(): Promise<void> {
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        await this.updateStartStep(_.get(this, 'props.user.attributes.startupStep') + 1);
    }

    public async updateStartStep(step: number): Promise<void> {
        if (!this.props.user) return;

        this.setState({
            startupStep: step
        });

        const attributes = this.props.user.attributes;
        if (step === null) {
            delete attributes.startupStep;
            delete attributes.startupSteps;
            await UserService.deleteAttribute('startupStep');
        } else {
            attributes.startupStep = step;
        }
        await this.handleUpdateUserAttributes(attributes);
    }

    public hasData(user: FullUserModel): boolean {
        const incomes: number = _.sumBy(user.cashFlow, 'income');
        const expenses: number = _.sumBy(user.cashFlow, 'expense');
        const debts: number = user.debts.length;
        const assets: number = user.holdings.length + user.realEstate.length + user.investments.length;

        return (
            user.currentNetWorth !== 0 ||
            user.investments.length > 0 ||
            user.debts.length > 0 ||
            user.institutions.length > 0 ||
            incomes !== 0 ||
            expenses !== 0 ||
            assets !== 0 ||
            debts !== 0
        );
    }

    public async showSignup(): Promise<void> {
        const attributes = this.state.user.attributes;
        delete attributes.hideSignup;
        await UserService.deleteAttribute('hideSignup');
        await this.handleUpdateUserAttributes(attributes);
    }

    public goToCard(cardId: number): void {
        void NavigationService.goTo(this.props.history, `/card/${cardId}`, 'Open Card');
    }

    private hideNextStep(): void {
        this.setState({
            showNextStep: false
        });
    }

    private restartHeartBeat(): void {
        clearInterval(this.instanceInterval);
    }

    private updateJaegerContext(context: JaegerContext): void {
        this.setState({
            jaegerContext: context
        });
    }

    private firstDrag(): void {
        void this.mentorFactory.processAction('first-drag');
    }
}

export default connect(Board.mapStateToProps, Board.mapDispatchToProps)(Board);
